import {createApi} from "@reduxjs/toolkit/query/react"
import {
    IBranch,
    IBranchMore,
    ILocalizedBranchText,
    IParamsAllBranchesList,
    IParamsBranchList,
    IBranchScheduleId,
    IBranchScheduleZoneItem,
    IResponseBranchScheduleList,
    IResponseBranchScheduleZoneList,
    IResponseBranchList
} from "./BranchTypes"
import {IBasicResponse} from "../../types/api"
import baseQuery from "../../utils/apiIIKOConfig"
import {
    IBranchPaymentClickParams,
    IBranchPaymentMulticardParams,
    IBranchPaymentPaymeParams,
    IBranchAuditLogListParams,
    IBranchScheduleCreateParams,
    IBranchScheduleDeleteParams,
    IBranchScheduleListParams,
    IResponseBranchAuditLogList,
    IBranchScheduleUpdateParams,
    IBranchShippingParams,
    IBranchSyncResponse, IBranchUpdateDataParams,
    IBranchUpdateSettingsParams
} from "./BranchApiTypes"
import {
    getBranchEffectiveClosedTime,
    getBranchEffectiveOpenTime,
    getBranchEffectiveServiceModes,
    getBranchEffectiveStatus,
    toBranchDeliveryZoneApiValue
} from "./branchUtils"
import {getPaginationDecision} from "../../utils/pagination"
import {translateUi} from "../../i18n/translateUi"

const BRANCH_LIST_PAGE_SIZE = 100

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value)

const isScheduleListItem = (value: unknown): value is IBranchScheduleZoneItem => {
    if (!isRecord(value) || !isRecord(value.branch)) return false

    return ((typeof value.id === "string" && value.id.trim().length > 0)
        || (typeof value.id === "number" && Number.isFinite(value.id)))
        && typeof value.branch.id === "string" && value.branch.id.trim().length > 0
}

const isScheduleListPage = (value: unknown, requestedPage: number): value is IResponseBranchScheduleZoneList =>
    isRecord(value)
    && value.page === requestedPage
    && Number.isSafeInteger(value.size) && Number(value.size) > 0 && Number(value.size) <= BRANCH_LIST_PAGE_SIZE
    && Number.isSafeInteger(value.total_elements) && Number(value.total_elements) >= 0
    && Array.isArray(value.content) && value.content.length <= Number(value.size)
    && value.content.every(isScheduleListItem)

const getLocalizedText = (value: ILocalizedBranchText) => {
    if (!value) return ""
    if (typeof value === "string") return value

    return value.ru || value.uz || Object.values(value).find(Boolean) || ""
}

const mapBranchListItemToBranch = (branch: IBranchMore): IBranch => ({
    id: branch.id,
    name: getLocalizedText(branch.name),
    address: getLocalizedText(branch.address),
    location: branch.location,
    status: getBranchEffectiveStatus(branch) || {
        id: "CLOSED",
        title: translateUi("Статус не указан")
    },
    picture_url: branch.picture_url,
    open_time: getBranchEffectiveOpenTime(branch),
    closed_time: getBranchEffectiveClosedTime(branch),
    phone_number: branch.phone_number || null,
    support_chat_url: branch.support_chat_url,
    delivery_duration_minutes: branch.delivery_duration_minutes,
    pickup_duration_minutes: branch.pickup_duration_minutes,
    service_mode: getBranchEffectiveServiceModes(branch)
})

export const branchApi = createApi({
    reducerPath: "branchApi",
    baseQuery,
    tagTypes: ["branches", "branchSchedules"],
    endpoints: build => ({
        getBranches: build.query<IBasicResponse<IBranch[]>, void>({
            queryFn: async (_arg, _queryApi, _extraOptions, fetchWithBQ) => {
                const branchesById = new Map<string, IBranch>()
                const seenSignatures = new Set<string>()
                let page = 1
                let responseMeta: Omit<IBasicResponse<IResponseBranchList>, "data"> = {
                    success: true
                }

                while (true) {
                    const response = await fetchWithBQ({
                        method: "POST",
                        url: "v1/branches/list",
                        body: {page, size: BRANCH_LIST_PAGE_SIZE}
                    })

                    if (response.error) {
                        return {error: response.error}
                    }

                    const responseData = response.data as IBasicResponse<IResponseBranchList>
                    if (!responseData?.success || !responseData.data) {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: responseData?.message || translateUi("Не удалось загрузить список филиалов")
                            }
                        }
                    }

                    const pageData = responseData.data
                    const content = pageData.content || []
                    const decision = getPaginationDecision({
                        requestedPage: page,
                        pageSize: BRANCH_LIST_PAGE_SIZE,
                        loadedCount: branchesById.size,
                        pageData,
                        seenSignatures,
                        getItemKey: (branch) => branch.id
                    })

                    responseMeta = {
                        success: responseData.success,
                        message: responseData.message
                    }
                    if (decision.type === "error") {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: translateUi("Не удалось загрузить полный список филиалов")
                            }
                        }
                    }

                    content.forEach((branch) => branchesById.set(branch.id, mapBranchListItemToBranch(branch)))
                    if (decision.type === "done") {
                        break
                    }

                    seenSignatures.add(decision.signature)
                    page = decision.nextPage
                }

                return {
                    data: {
                        ...responseMeta,
                        data: Array.from(branchesById.values())
                    }
                }
            },
            providesTags: ["branches"]
        }),
        getBranchesList: build.query<IBasicResponse<IResponseBranchList>, IParamsBranchList>({
            query: ({page, size, time, weekday}) => {
                const body = {
                    page,
                    size,
                    ...(time ? {time} : {}),
                    ...(weekday ? {weekday} : {})
                }

                return {
                    method: "POST",
                    url: `v1/branches/list`,
                    body
                }
            },
            providesTags: ["branches"]
        }),
        getAllBranchesList: build.query<IBasicResponse<IResponseBranchList>, IParamsAllBranchesList>({
            queryFn: async ({time, weekday}, _queryApi, _extraOptions, fetchWithBQ) => {
                const branchesById = new Map<string, IBranchMore>()
                const seenSignatures = new Set<string>()
                let page = 1
                let responseMeta: Omit<IBasicResponse<IResponseBranchList>, "data"> = {success: true}

                while (true) {
                    const response = await fetchWithBQ({
                        method: "POST",
                        url: "v1/branches/list",
                        body: {
                            page,
                            size: BRANCH_LIST_PAGE_SIZE,
                            ...(time ? {time} : {}),
                            ...(weekday ? {weekday} : {})
                        }
                    })

                    if (response.error) {
                        return {error: response.error}
                    }

                    const responseData = response.data as IBasicResponse<IResponseBranchList>
                    if (!responseData?.success || !responseData.data) {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: responseData?.message || translateUi("Не удалось загрузить список филиалов")
                            }
                        }
                    }

                    const pageData = responseData.data
                    const content = pageData.content || []
                    const decision = getPaginationDecision({
                        requestedPage: page,
                        pageSize: BRANCH_LIST_PAGE_SIZE,
                        loadedCount: branchesById.size,
                        pageData,
                        seenSignatures,
                        getItemKey: (branch) => branch.id
                    })

                    if (decision.type === "error") {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: translateUi("Не удалось загрузить полный список филиалов")
                            }
                        }
                    }

                    content.forEach((branch) => branchesById.set(branch.id, branch))
                    responseMeta = {
                        success: responseData.success,
                        message: responseData.message
                    }

                    if (decision.type === "done") {
                        break
                    }

                    seenSignatures.add(decision.signature)
                    page = decision.nextPage
                }

                const content = Array.from(branchesById.values())
                return {
                    data: {
                        ...responseMeta,
                        data: {
                            content,
                            page: 1,
                            size: content.length,
                            total_elements: content.length
                        }
                    }
                }
            },
            providesTags: ["branches"]
        }),
        getBranchAuditLogs: build.query<IBasicResponse<IResponseBranchAuditLogList>, IBranchAuditLogListParams>({
            query: ({page, size, branch_id, date_from, date_to}) => ({
                method: "POST",
                url: "v1/branches/audit-logs/list",
                body: {
                    page,
                    size,
                    ...(branch_id ? {branch_id} : {}),
                    ...(date_from ? {date_from} : {}),
                    ...(date_to ? {date_to} : {})
                }
            }),
            providesTags: ["branches"]
        }),
        syncBranches: build.mutation<IBasicResponse<IBranchSyncResponse>, void>({
            query: () => ({
                method: "POST",
                url: `v1/branches/sync`,
            }),
            invalidatesTags: ["branches"]
        }),
        updateDataById: build.mutation<IBasicResponse<void>, IBranchUpdateDataParams>({
            query: ({id, name, address, picture_url}) => ({
                method: "PATCH",
                url: `v1/branches/${id}`,
                body: {
                    name,
                    address,
                    picture_url
                }
            }),
            invalidatesTags: ["branches"]
        }),
        getBranchSchedules: build.query<IBasicResponse<IResponseBranchScheduleList>, IBranchScheduleListParams>({
            query: ({page, size, branch_id}) => ({
                method: "POST",
                url: `v1/branches/schedules/list`,
                body: {
                    page,
                    size,
                    ...(branch_id ? {branch_id} : {})
                }
            }),
            providesTags: ["branchSchedules"]
        }),
        getAllBranchSchedules: build.query<
            IBasicResponse<IResponseBranchScheduleZoneList>,
            Pick<IBranchScheduleListParams, "branch_id">
        >({
            queryFn: async ({branch_id}, _queryApi, _extraOptions, fetchWithBQ) => {
                const schedulesById = new Map<string, IBranchScheduleZoneItem>()
                const seenSignatures = new Set<string>()
                let page = 1
                let totalElements: number | undefined
                let message: string | undefined
                const invalidResponse = () => ({
                    error: {
                        status: "CUSTOM_ERROR" as const,
                        error: translateUi("Не удалось загрузить зоны филиалов")
                    }
                })

                while (true) {
                    const response = await fetchWithBQ({
                        method: "POST",
                        url: "v1/branches/schedules/list",
                        body: {
                            page,
                            size: BRANCH_LIST_PAGE_SIZE,
                            ...(branch_id ? {branch_id} : {})
                        }
                    })
                    if (response.error) return {error: response.error}

                    const responseData: unknown = response.data
                    if (!isRecord(responseData) || responseData.success !== true
                        || !isScheduleListPage(responseData.data, page)) {
                        return invalidResponse()
                    }

                    const pageData = responseData.data
                    if (totalElements !== undefined && totalElements !== pageData.total_elements) {
                        return invalidResponse()
                    }
                    totalElements = pageData.total_elements
                    const decision = getPaginationDecision({
                        requestedPage: page,
                        pageSize: pageData.size,
                        loadedCount: schedulesById.size,
                        pageData,
                        seenSignatures,
                        getItemKey: schedule => schedule.id
                    })
                    if (decision.type === "error") return invalidResponse()

                    pageData.content.forEach(schedule => schedulesById.set(String(schedule.id), schedule))
                    if (schedulesById.size > totalElements) return invalidResponse()
                    message = typeof responseData.message === "string" ? responseData.message : undefined

                    if (decision.type === "done") {
                        if (schedulesById.size !== totalElements) return invalidResponse()
                        break
                    }

                    seenSignatures.add(decision.signature)
                    page = decision.nextPage
                }

                const content = Array.from(schedulesById.values())
                return {
                    data: {
                        success: true,
                        ...(message ? {message} : {}),
                        data: {content, page: 1, size: content.length, total_elements: content.length}
                    }
                }
            },
            providesTags: ["branches", "branchSchedules"]
        }),
        createBranchSchedule: build.mutation<IBasicResponse<{id: IBranchScheduleId}>, IBranchScheduleCreateParams>({
            query: ({branch_id, branch_status, opening_time, closing_time, weekday_list, service_mode_list, delivery_zone}) => ({
                method: "POST",
                url: `v1/branches/schedules`,
                body: {
                    branch_id,
                    branch_status,
                    opening_time,
                    closing_time,
                    weekday_list,
                    service_mode_list,
                    delivery_zone: toBranchDeliveryZoneApiValue(delivery_zone)
                }
            }),
            invalidatesTags: ["branches", "branchSchedules"]
        }),
        updateBranchSchedule: build.mutation<IBasicResponse<void>, IBranchScheduleUpdateParams>({
            query: ({id, branch_status, opening_time, closing_time, weekday_list, service_mode_list, delivery_zone}) => ({
                method: "PUT",
                url: `v1/branches/schedules/${id}`,
                body: {
                    branch_status,
                    opening_time,
                    closing_time,
                    weekday_list,
                    service_mode_list,
                    delivery_zone: toBranchDeliveryZoneApiValue(delivery_zone)
                }
            }),
            invalidatesTags: ["branches", "branchSchedules"]
        }),
        deleteBranchSchedule: build.mutation<IBasicResponse<void>, IBranchScheduleDeleteParams>({
            query: ({id}) => ({
                method: "DELETE",
                url: `v1/branches/schedules/${id}`,
            }),
            invalidatesTags: ["branches", "branchSchedules"]
        }),
        updateSettingsById: build.mutation<IBasicResponse<void>, IBranchUpdateSettingsParams>({
            query: ({id, phone_number, support_chat_url, delivery_duration_minutes, pickup_duration_minutes, location}) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/settings`,
                body: {
                    phone_number,
                    support_chat_url,
                    delivery_duration_minutes,
                    pickup_duration_minutes,
                    ...(location ? {location} : {})
                }
            }),
            invalidatesTags: ["branches"]
        }),
        updateShippingById: build.mutation<IBasicResponse<void>, IBranchShippingParams>({
            query: ({id, shipping_id, telegram_group_chat_id, max_active_orders_per_courier, courier_acceptance_radius_meters, courier_completion_radius_meters}) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/shipping`,
                body: {
                    shipping_id,
                    telegram_group_chat_id,
                    max_active_orders_per_courier,
                    courier_acceptance_radius_meters,
                    courier_completion_radius_meters
                }
            }),
            invalidatesTags: ["branches"]
        }),
        updatePaymentPaymeById: build.mutation<IBasicResponse<void>, IBranchPaymentPaymeParams>({
            query: ({id, payme_client_id, payme_client_key}) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/payment/payme`,
                body: {
                    payme_client_id,
                    payme_client_key
                }
            }),
            invalidatesTags: ["branches"]
        }),
        updatePaymentClickById: build.mutation<IBasicResponse<void>, IBranchPaymentClickParams>({
            query: ({id, ...body}) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/payment/click`,
                body
            }),
            invalidatesTags: ["branches"]
        }),
        updatePaymentMulticardById: build.mutation<IBasicResponse<void>, IBranchPaymentMulticardParams>({
            query: ({id, store_id}) => ({
                method: "PATCH",
                url: `/v1/branches/${id}/payment/multicard`,
                body: {
                    store_id
                }
            }),
            invalidatesTags: ["branches"]
        })
    })
})

export const {
    useCreateBranchScheduleMutation,
    useDeleteBranchScheduleMutation,
    useGetBranchAuditLogsQuery,
    useGetBranchSchedulesQuery,
    useGetAllBranchSchedulesQuery,
    useGetAllBranchesListQuery,
    useGetBranchesListQuery,
    useGetBranchesQuery,
    useSyncBranchesMutation,
    useUpdateBranchScheduleMutation,
    useUpdateDataByIdMutation,
    useUpdateSettingsByIdMutation,
    useUpdateShippingByIdMutation,
    useUpdatePaymentClickByIdMutation,
    useUpdatePaymentPaymeByIdMutation,
    useUpdatePaymentMulticardByIdMutation
} = branchApi
